import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';

import type { ParentsBulkImportJob } from '~entities/parent/api/get-parents-bulk-import-status';

import { Alert, AlertDescription } from '~shared/shadcn/ui/alert';
import { Button } from '~shared/shadcn/ui/button';

import { BulkImportActionRow } from './bulk-import-action-row';
import { downloadImportIssuesCsv } from './export-import-issues-csv';
import {
  buildActionRowContextFromUnresolved,
  buildActionRowContextFromWarning,
  type ImportActionRowContext,
  reasonLabel,
} from './import-resolution-playbook';

interface BulkImportResultsPanelProps {
  job: ParentsBulkImportJob;
  showViewFullPageLink?: boolean;
}

export function BulkImportResultsPanel({
  job,
  showViewFullPageLink = false,
}: BulkImportResultsPanelProps) {
  const result = job.result;
  const actionRows = useMemo(() => {
    if (!result) return [] as ImportActionRowContext[];
    const warnings = (result.importWarnings ?? []).map((w) =>
      buildActionRowContextFromWarning({
        ...w,
        manualResolutionKey: w.manualResolutionKey ?? w.reason,
      }),
    );
    const unresolved = (result.duplicateUnresolved ?? []).map((d) =>
      buildActionRowContextFromUnresolved({
        ...d,
        manualResolutionKey: d.manualResolutionKey ?? d.reason,
      }),
    );
    return [...warnings, ...unresolved];
  }, [result]);

  const reasonBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of actionRows) {
      counts.set(row.reason, (counts.get(row.reason) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [actionRows]);

  if (!result) {
    return (
      <Alert>
        <AlertDescription className='text-sm text-muted-foreground'>
          Import completed but no result details were returned.
        </AlertDescription>
      </Alert>
    );
  }

  const warningsCount = result.importWarnings?.length ?? 0;
  const attentionCount = result.duplicateUnresolved?.length ?? 0;

  return (
    <div className='space-y-4'>
      <Alert>
        <AlertDescription>
          <div className='font-medium'>Import summary</div>
          <div className='mt-1 text-sm text-muted-foreground'>
            Student rows succeeded: {result.success} · Failed: {result.failed}
            {warningsCount > 0 ? (
              <span className='block pt-1'>Warnings: {warningsCount}</span>
            ) : null}
            {attentionCount > 0 ? (
              <span className='block pt-1'>
                Needs attention: {attentionCount}
              </span>
            ) : null}
            {result.parentsCreated != null || result.parentsReused != null ? (
              <span className='block pt-1'>
                New parents: {result.parentsCreated ?? 0} · Existing parents
                (students added): {result.parentsReused ?? 0}
              </span>
            ) : null}
            {typeof result.sameParentEmailMergedRowCount === 'number' ? (
              <span className='block pt-1'>
                Same parent email — merged extra rows:{' '}
                {result.sameParentEmailMergedRowCount}
              </span>
            ) : null}
            {job.durationMs != null ? (
              <span className='block pt-1 text-xs'>
                Server duration: {Math.round(job.durationMs / 1000)}s
              </span>
            ) : null}
          </div>
          {result.batchId ? (
            <div className='mt-2 flex flex-wrap gap-2 text-xs'>
              <span className='text-muted-foreground'>
                Onboarding batch: {result.batchId}
              </span>
              <Link
                to='/parents/csv-batch-processing/$id'
                params={{ id: result.batchId }}
                className='text-primary underline'
              >
                View onboarding email batch
              </Link>
            </div>
          ) : null}
        </AlertDescription>
      </Alert>

      {showViewFullPageLink && job.jobId !== 'immediate' ? (
        <Button variant='outline' size='sm' asChild>
          <Link
            to='/parents/bulk-upload/results/$jobId'
            params={{ jobId: job.jobId }}
          >
            View full results page
          </Link>
        </Button>
      ) : null}

      {reasonBreakdown.length > 0 && (
        <div className='space-y-2'>
          <div className='text-sm font-medium'>Why rows need attention</div>
          <ul className='list-disc space-y-1 rounded-md border bg-muted/40 p-3 pl-7 text-sm'>
            {reasonBreakdown.map(([reason, count]) => (
              <li key={reason}>
                {count} row{count === 1 ? '' : 's'} — {reasonLabel(reason)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.errors.length > 0 && (
        <div className='space-y-2'>
          <div className='text-sm font-medium text-amber-800'>
            Messages / errors ({result.errors.length})
          </div>
          <div className='max-h-36 space-y-1 overflow-y-auto rounded-md border bg-muted/40 p-3 text-sm'>
            {result.errors.slice(0, 50).map((line, i) => (
              <div key={i}>• {line}</div>
            ))}
          </div>
        </div>
      )}

      {warningsCount > 0 && (
        <div className='space-y-3'>
          <div className='text-sm font-semibold'>Import warnings ({warningsCount})</div>
          <p className='text-xs text-muted-foreground'>
            These students were created but may need a quick review.
          </p>
          <div className='space-y-3'>
            {actionRows
              .filter((r) => r.kind === 'warning')
              .map((row, idx) => (
                <BulkImportActionRow key={`w-${idx}`} row={row} />
              ))}
          </div>
        </div>
      )}

      {attentionCount > 0 && (
        <div className='space-y-3'>
          <div className='text-sm font-semibold'>
            Needs your attention ({attentionCount})
          </div>
          <p className='text-xs text-muted-foreground'>
            These rows were not created automatically. Use the actions below or
            fix your CSV and re-import.
          </p>
          <div className='space-y-3'>
            {actionRows
              .filter((r) => r.kind === 'attention')
              .map((row, idx) => (
                <BulkImportActionRow key={`a-${idx}`} row={row} />
              ))}
          </div>
        </div>
      )}

      {actionRows.length > 0 ? (
        <Button
          variant='outline'
          size='sm'
          onClick={() =>
            downloadImportIssuesCsv(
              actionRows,
              `bulk-import-issues-${job.jobId}.csv`,
            )
          }
        >
          Download issues CSV
        </Button>
      ) : null}
    </div>
  );
}
